import { logger } from '@/core/logger';
import { asyncStorage } from '@/core/storage';
import { I18nManager } from 'react-native';
import { SupportedLanguage } from '../types/i18n';

const LANGUAGE_STORAGE_KEY = '@bloom_language';
const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

// RTL languages that require right-to-left layout
const RTL_LANGUAGES: SupportedLanguage[] = ['ar'];

/**
 * Check if a language requires RTL layout
 */
export const isRTLLanguage = (language: SupportedLanguage): boolean => {
  return RTL_LANGUAGES.includes(language);
};

/**
 * Web-only sync read of the saved language.
 * AsyncStorage on web stores this key in localStorage, so UAE Pass can read it
 * without yielding before the same-tab redirect.
 */
export function peekStoredLanguage(): SupportedLanguage {
  try {
    if (typeof localStorage === 'undefined') {
      return DEFAULT_LANGUAGE;
    }
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === 'en' || stored === 'ar') {
      return stored;
    }
  } catch (error) {
    logger.warn('i18n', 'Failed to peek stored language:', error);
  }
  return DEFAULT_LANGUAGE;
}

/**
 * Get the current app language from storage
 */
export const getCurrentLanguage = async (): Promise<SupportedLanguage> => {
  try {
    const storedLanguage = await asyncStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (storedLanguage && (storedLanguage === 'en' || storedLanguage === 'ar')) {
      return storedLanguage as SupportedLanguage;
    }
  } catch (error) {
    logger.warn('i18n', 'Failed to get stored language:', error);
  }
  return DEFAULT_LANGUAGE;
};

/**
 * Set the app language and update RTL layout
 */
export const setLanguage = async (language: SupportedLanguage): Promise<void> => {
  try {
    // RTL mirroring is driven by I18nProvider's root `direction` style (reacts
    // immediately, no native restart needed) — no I18nManager.forceRTL() call
    // or app reload required here.
    await asyncStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch (error) {
    logger.error('i18n', 'Failed to set language:', error);
    throw error;
  }
};

/**
 * Initialize I18nManager settings on app start
 */
export const initializeI18n = async (): Promise<SupportedLanguage> => {
  try {
    I18nManager.allowRTL(true);
    return await getCurrentLanguage();
  } catch (error) {
    logger.error('i18n', 'Failed to initialize i18n:', error);
    return DEFAULT_LANGUAGE;
  }
};

/**
 * Toggle between supported languages
 */
export const toggleLanguage = async (currentLanguage: SupportedLanguage): Promise<SupportedLanguage> => {
  const newLanguage: SupportedLanguage = currentLanguage === 'en' ? 'ar' : 'en';
  await setLanguage(newLanguage);
  return newLanguage;
};